import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import enviro from 'enviro';
import { Map as ImmutableMap } from 'immutable';
import PortalIdParser from 'PortalIdParser';
import { INSERT_HUBSPOT_VIDEO } from 'rich-text-lib/constants/usageTracking';
import { userInfoSync } from 'hub-http/userInfo';
import { callIfPossible } from 'UIComponents/core/Functions';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UITile from 'UIComponents/tile/UITile';
import UICheckbox from 'UIComponents/input/UICheckbox';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Tracker } from '../utils/usageTracker';
import { INLINE_VIDEO_CONSTANTS, VIDEO_CONSTANTS } from './utils/constants';
import { notifyOfVideoCreation } from './api/FileManagerApi';
import { buildShardedVideoShareUrl, isDefaultHubSpotHostedUrl } from './utils/createGoVideoUrl';
import { encodeUrl } from './utils/libUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const {
  DEFAULT_VIDEO_WIDTH,
  DRAFT_ATOMIC_TYPE_VIDEO,
  EMBED_SCRIPT_URL,
  SALES_PROD_CLIENT_ID,
  SALES_QA_CLIENT_ID,
  SERVICE_HUB_PROD_CLIENT_ID,
  SERVICE_HUB_QA_CLIENT_ID
} = VIDEO_CONSTANTS;
const {
  DRAFT_ATOMIC_TYPE_INLINE_VIDEO
} = INLINE_VIDEO_CONSTANTS;
const VIDEO_DOMAIN_SHARDING_GATE = 'DAM:1to1Video:DomainSharding';
function getClientId(isInServiceHub) {
  const isProd = enviro.isProd();
  if (isInServiceHub) {
    return isProd ? SERVICE_HUB_PROD_CLIENT_ID : SERVICE_HUB_QA_CLIENT_ID;
  }
  return isProd ? SALES_PROD_CLIENT_ID : SALES_QA_CLIENT_ID;
}
class GoVideoContainer extends PureComponent {
  constructor(props) {
    super(props);
    this.isVideoPageDomainShardingUngated = () => {
      if (enviro.getHublet() === 'eu1') {
        return true;
      }
      try {
        const auth = userInfoSync();
        return auth.gates.includes(VIDEO_DOMAIN_SHARDING_GATE);
      } catch (err) {
        console.warn('userInfoSync threw error, assuming video page domain sharding is not ungated');
        return false;
      }
    };
    this.handleInitiateVidyardComponent = () => {
      const {
        recipientEmail
      } = this.props;
      const vidyardInstance = window.Vidyard;
      if (vidyardInstance) {
        if (this.initiateComponentTimeout) {
          clearTimeout(this.initiateComponentTimeout);
        }
        this.setState({
          isLoading: false
        });
        const clientId = getClientId(this.props.isInServiceHub);
        const library = vidyardInstance.goVideo.createLibrary(document.getElementById('GoVideo'), {
          clientId,
          recipientEmail
        });
        library.on('player:created', this.handleConfirm);
      } else {
        this.initiateComponentTimeout = setTimeout(this.handleInitiateVidyardComponent, 100);
      }
    };
    this.handleVideoCreated = () => {
      notifyOfVideoCreation().catch(() => {
        console.warn('Failed to notify of video creation');
      });
    };
    this.handleConfirm = ({
      player: {
        custom_id,
        name,
        uuid
      },
      embed_codes: {
        sharing_page,
        thumbnail,
        base_url
      },
      videos
    }) => {
      const {
        onChange,
        onClose,
        onConfirm,
        trackingHandlers,
        isInlineVideo
      } = this.props;
      if (onConfirm) {
        const url = this.isVideoPageDomainShardingUngated() && isDefaultHubSpotHostedUrl(sharing_page) ? buildShardedVideoShareUrl(uuid) : sharing_page;
        onConfirm({
          player: {
            custom_id,
            sharing_page: url
          },
          videos
        });
        return;
      }
      const url = this.isVideoPageDomainShardingUngated() && isDefaultHubSpotHostedUrl(sharing_page) ? buildShardedVideoShareUrl(uuid, {
        appendPortalId: true
      }) : `${sharing_page}portalId=${PortalIdParser.get()}`;
      const videoBlockMetadata = ImmutableMap({
        atomicType: isInlineVideo ? DRAFT_ATOMIC_TYPE_INLINE_VIDEO : DRAFT_ATOMIC_TYPE_VIDEO,
        videoTitle: this.state.shouldInsertTitle ? name : undefined,
        url,
        thumbnailSrc: encodeUrl(thumbnail),
        uuid,
        customId: custom_id,
        width: DEFAULT_VIDEO_WIDTH,
        base_url
      });
      onChange(videoBlockMetadata);
      Tracker.track('prosemirrorVideo', {
        action: INSERT_HUBSPOT_VIDEO,
        method: 'toolbar',
        watchPageUrl: sharing_page,
        videoTitle: name
      });
      callIfPossible(trackingHandlers.onInsertVideo);
      this.handleVideoCreated();
      onClose();
    };
    this.state = {
      hasCreatedVideoOnce: false,
      isLoading: true,
      shouldInsertTitle: true
    };
  }
  componentDidMount() {
    this.fetchVidyardEmbedScript();
    this.handleInitiateVidyardComponent();
  }
  componentWillUnmount() {
    if (this.initiateComponentTimeout) {
      clearTimeout(this.initiateComponentTimeout);
    }
  }
  fetchVidyardEmbedScript() {
    if (!window.Vidyard) {
      const vidyardEmbedScript = document.createElement('script');
      vidyardEmbedScript.src = EMBED_SCRIPT_URL;
      if (document && document.body) {
        document.body.appendChild(vidyardEmbedScript);
      }
    }
  }
  render() {
    const {
      isLoading
    } = this.state;
    return /*#__PURE__*/_jsxs("div", {
      className: "p-top-4 p-bottom-10",
      style: {
        width: '100%'
      },
      children: [this.props.showTitleCheckBox && /*#__PURE__*/_jsx(UICheckbox, {
        className: "m-bottom-3",
        defaultChecked: true,
        checked: this.state.shouldInsertTitle,
        onChange: ({
          target: {
            checked
          }
        }) => {
          this.setState({
            shouldInsertTitle: checked
          });
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "draftPlugins.goVideoModal.showVideoTitle"
        })
      }), /*#__PURE__*/_jsx(UITile
      // @ts-expect-error ts-migrate(2322) FIXME: Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
      , {
        className: isLoading ? 'hidden' : null,
        distance: "flush",
        closeable: false,
        children: /*#__PURE__*/_jsx("div", {
          id: "GoVideo"
        })
      }), isLoading && /*#__PURE__*/_jsx(UILoadingSpinner, {
        grow: true
      })]
    });
  }
}
GoVideoContainer.propTypes = {
  isInServiceHub: PropTypes.bool,
  onChange: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  trackingHandlers: PropTypes.object.isRequired,
  recipientEmail: PropTypes.string,
  isInlineVideo: PropTypes.bool,
  showTitleCheckBox: PropTypes.bool
};
GoVideoContainer.defaultProps = {
  trackingHandlers: {},
  isInlineVideo: false,
  showTitleCheckBox: false
};
export default GoVideoContainer;